import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePortal } from "../../../context/PortalContext";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import {
  buildBookingSpecializationOptions,
  getServicesForSpecializationAtBranch,
  getBranchesForService,
  formatServiceBookingLabel,
} from "../../../utils/serviceCatalog";
import { filterDoctorsForBooking } from "../../../utils/doctorCatalog";
import { normalizeRole } from "../../../utils/roles";
import { getApiOrigin } from "../../../utils/apiBase";
import L from "../../../constants/baysportLabels";
import logo from "../../../assets/logo.svg";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  Skeleton,
  ToastProvider,
  useToast,
} from "../../ui";

const STEPS = [
  L.direction,
  "Услуга",
  "Зал",
  L.trainer,
  "Дата и время",
  "Подтверждение",
];

const STEP_HINTS = [
  `Выберите ${L.direction.toLowerCase()} — покажем подходящие занятия`,
  "Выберите тип занятия",
  "Выберите зал или зал",
  `Выберите ${L.trainer.toLowerCase()} или ближайшее свободное время`,
  "Выберите удобную дату и время",
  "Проверьте данные и подтвердите запись",
];

const SPEC_ICONS = [
  { key: "пульмон", icon: "🫁" },
  { key: "терап", icon: "🩺" },
  { key: "гинек", icon: "🌸" },
  { key: "кардио", icon: "❤️" },
  { key: "стомат", icon: "🦷" },
  { key: "педиатр", icon: "👶" },
  { key: "дермат", icon: "✨" },
  { key: "офтальм", icon: "👁" },
  { key: "лор", icon: "👂" },
  { key: "отоларинг", icon: "👂" },
  { key: "невролог", icon: "⚡" },
  { key: "уролог", icon: "💧" },
  { key: "гастро", icon: "🫃" },
  { key: "ортопед", icon: "🦴" },
  { key: "травмат", icon: "🦴" },
  { key: "псих", icon: "🧠" },
  { key: "эндокрин", icon: "🧬" },
  { key: "хирург", icon: "🩹" },
  { key: "бокс", icon: "🥊" },
  { key: "единоб", icon: "🤼" },
  { key: "фитнес", icon: "💪" },
  { key: "йога", icon: "🧘" },
  { key: "плаван", icon: "🏊" },
  { key: "футбол", icon: "⚽" },
  { key: "танц", icon: "💃" },
  { key: "гимнаст", icon: "🤸" },
  { key: "карат", icon: "🥋" },
  { key: "теннис", icon: "🎾" },
];

function specIcon(name) {
  const key = String(name || "").toLowerCase();
  for (const { key: k, icon } of SPEC_ICONS) {
    if (key.includes(k)) return icon;
  }
  return "🩺";
}

function serviceCountLabel(n) {
  const x = Number(n) || 0;
  if (x === 1) return "1 услуга";
  if (x >= 2 && x <= 4) return `${x} услуги`;
  return `${x} услуг`;
}

function ClientBookProgress({ step, label }) {
  const pct = ((step + 1) / STEPS.length) * 100;
  return (
    <div className={s.clientBookProgress}>
      <div
        className={s.clientBookProgressTrack}
        role="progressbar"
        aria-valuenow={step + 1}
        aria-valuemin={1}
        aria-valuemax={STEPS.length}
      >
        <div className={s.clientBookProgressFill} style={{ width: `${pct}%` }} />
      </div>
      <p className={s.clientBookProgressMeta}>
        Шаг {step + 1} из {STEPS.length}
      </p>
      <h2 className={s.clientBookProgressTitle}>{label}</h2>
    </div>
  );
}

function BookStepper({ step, onGo }) {
  return (
    <ol className={s.portalBookStepper} aria-label="Шаги записи">
      {STEPS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        const itemClass = active
          ? s.portalBookStepperItemActive
          : done
            ? s.portalBookStepperItemDone
            : s.portalBookStepperItem;
        const inner = (
          <>
            <span className={s.portalBookStepperNum}>
              {done ? "✓" : i + 1}
            </span>
            <span className={s.portalBookStepperLabel}>{label}</span>
          </>
        );
        if (done) {
          return (
            <li key={label} className={itemClass}>
              <button type="button" className={s.portalBookStepperBtn} onClick={() => onGo(i)}>
                {inner}
              </button>
            </li>
          );
        }
        return (
          <li key={label} className={itemClass} aria-current={active ? "step" : undefined}>
            {inner}
          </li>
        );
      })}
    </ol>
  );
}

const API_ORIGIN = getApiOrigin();

function doctorPhotoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (photoUrl.startsWith("http")) return photoUrl;
  return `${API_ORIGIN}${photoUrl}`;
}

function reviewCountLabel(n) {
  const x = Number(n) || 0;
  if (x === 0) return "нет отзывов";
  const mod10 = x % 10;
  const mod100 = x % 100;
  if (mod10 === 1 && mod100 !== 11) return `${x} отзыв`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `${x} отзыва`;
  }
  return `${x} отзывов`;
}

function resetFrom(level, prev) {
  const next = { ...prev };
  if (level <= 1) next.specializationId = "";
  if (level <= 2) next.serviceId = "";
  if (level <= 3) next.branchId = "";
  if (level <= 4) {
    next.doctorId = "";
    next.anyDoctor = false;
  }
  if (level <= 5) next.slotIso = "";
  return next;
}

function getDoctorsForServiceBranch(
  serviceId,
  branchId,
  doctors,
  specializationId = null,
) {
  return filterDoctorsForBooking(doctors, {
    serviceId,
    branchId,
    specializationId,
  });
}

function DoctorBookCard({
  doctor,
  selected,
  onSelect,
  showProfileLink = true,
  favoriteSlot = null,
}) {
  const photo = doctorPhotoSrc(doctor.photo_url);
  const review = doctor.latest_review;
  const count = Number(doctor.rating_count) || 0;
  const avg = doctor.rating_avg != null ? Number(doctor.rating_avg) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      className={
        selected ? s.bookWizardDoctorCardSelected : s.bookWizardDoctorCard
      }
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {favoriteSlot}
      <div className={s.bookWizardDoctorCardTop}>
        {photo ? (
          <img src={photo} alt="" className={s.bookWizardDoctorPhoto} />
        ) : (
          <div className={s.bookWizardDoctorPhotoPlaceholder}>
            {doctor.full_name?.[0] || "?"}
          </div>
        )}
        <div className={s.bookWizardDoctorInfo}>
          <div className={s.bookWizardDoctorName}>{doctor.full_name}</div>
          {doctor.specialization ? (
            <div className={s.bookWizardDoctorMeta}>{doctor.specialization}</div>
          ) : null}
          {doctor.experience_years ? (
            <div className={s.bookWizardDoctorMeta}>
              Стаж {doctor.experience_years} лет
            </div>
          ) : null}
          {avg != null ? (
            <div className={s.bookWizardDoctorRating}>
              ★ {avg.toFixed(1)}
              <span className={s.bookWizardDoctorMeta}>
                · {reviewCountLabel(count)}
              </span>
            </div>
          ) : count > 0 ? (
            <div className={s.bookWizardDoctorMeta}>{reviewCountLabel(count)}</div>
          ) : null}
        </div>
      </div>
      {review?.comment ? (
        <p className={s.bookWizardDoctorReview}>«{review.comment}»</p>
      ) : null}
      {showProfileLink && (count > 0 || doctor.description) ? (
        <div className={s.bookWizardDoctorActions}>
          <Link
            to={`/app/doctors/${doctor.id}`}
            className={s.bookWizardDoctorProfileLink}
            onClick={(e) => e.stopPropagation()}
          >
            Профиль и отзывы
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export function PatientBookContent({ embedded = false, appMode = false, onBooked } = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { clinic, subdomain } = usePortal();
  const { isAuth, role } = useSelector((state) => state.auth);
  const isClient = normalizeRole(role) === "client";
  const deepLinkApplied = useRef(false);

  const [step, setStep] = useState(0);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [aggregated, setAggregated] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsMessage, setSlotsMessage] = useState("");
  const [nearestSlotDate, setNearestSlotDate] = useState("");
  const slotsScanRef = useRef(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [specializationId, setSpecializationId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [anyDoctor, setAnyDoctor] = useState(false);
  const [date, setDate] = useState(toDateInputValue());
  const [slotIso, setSlotIso] = useState("");
  const [substituteHint, setSubstituteHint] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [dependentId, setDependentId] = useState("");
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  const dates = useMemo(() => {
    const list = [];
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 21; i++) {
      const x = new Date(d);
      x.setDate(d.getDate() + i);
      list.push({
        value: toDateInputValue(x),
        day: x.toLocaleDateString("ru-RU", { weekday: "short" }),
        num: x.getDate(),
      });
    }
    return list;
  }, []);

  useEffect(() => {
    setCatalogLoading(true);
    clinicAPI
      .getPublicClinicPortal()
      .then((r) => {
        setServices(r.services || []);
        setSpecializations(r.specializations || []);
        setBranches(r.branches || []);
        setDoctors(r.doctors || []);
      })
      .catch(() => toast.error("Не удалось загрузить данные клуба"))
      .finally(() => setCatalogLoading(false));
  }, [subdomain, toast]);

  useEffect(() => {
    if (!isAuth || !isClient) return;
    clinicAPI.getDependents().then((r) => setDependents(r.dependents || []));
    clinicAPI
      .getFavoriteDoctors()
      .then((r) =>
        setFavoriteIds(new Set((r.favorites || []).map((f) => f.doctor_id))),
      )
      .catch(() => {});
  }, [isAuth, isClient]);

  const bookableServices = useMemo(
    () => services.filter((sv) => (sv.doctor_ids || []).length > 0),
    [services],
  );

  const specializationOptions = useMemo(
    () => buildBookingSpecializationOptions(specializations, bookableServices),
    [specializations, bookableServices],
  );

  const servicesForSpec = useMemo(
    () =>
      getServicesForSpecializationAtBranch(
        bookableServices,
        specializationId,
        branchId,
      ),
    [bookableServices, specializationId, branchId],
  );

  const branchesForService = useMemo(
    () =>
      getBranchesForService(
        serviceId,
        branches,
        bookableServices,
        doctors,
        specializationId,
      ),
    [serviceId, branches, bookableServices, doctors, specializationId],
  );

  const doctorsForBooking = useMemo(
    () =>
      getDoctorsForServiceBranch(
        serviceId,
        branchId,
        doctors,
        specializationId,
      ),
    [serviceId, branchId, doctors, specializationId],
  );

  const aggregatedForBooking = useMemo(() => {
    const allowed = new Set(doctorsForBooking.map((d) => d.id));
    return aggregated.filter(({ doctor }) => allowed.has(doctor.id));
  }, [aggregated, doctorsForBooking]);

  const selectedSpecialization = specializationOptions.find(
    (sp) => sp.id === specializationId,
  );
  const selectedService = services.find((x) => x.id === serviceId);
  const selectedBranch = branches.find((x) => x.id === branchId);
  const selectedDoctor = doctors.find((x) => x.id === doctorId);

  useEffect(() => {
    if (catalogLoading || deepLinkApplied.current) return;

    const specParam = searchParams.get("spec");
    const docParam = searchParams.get("doctor");

    if (docParam) {
      const doc = doctors.find((d) => d.id === docParam);
      if (doc) {
        const svc =
          bookableServices.find(
            (sv) =>
              (doc.service_ids || []).includes(sv.id) &&
              (!doc.specialization_id ||
                sv.specialization_id === doc.specialization_id ||
                !sv.specialization_id),
          ) || bookableServices.find((sv) =>
            (doc.service_ids || []).includes(sv.id),
          );
        if (doc.specialization_id) setSpecializationId(doc.specialization_id);
        if (svc) setServiceId(svc.id);
        const br =
          (doc.branch_ids || []).map((id) => branches.find((b) => b.id === id))
            .filter(Boolean)[0] || branches.find((b) => b.is_default);
        if (br) setBranchId(br.id);
        setDoctorId(doc.id);
        setAnyDoctor(false);
        setStep(4);
        deepLinkApplied.current = true;
        return;
      }
    }

    if (specParam && specializationOptions.some((sp) => sp.id === specParam)) {
      setSpecializationId(specParam);
      setServiceId("");
      setDoctorId("");
      setAnyDoctor(false);
      setStep(1);
      deepLinkApplied.current = true;
    }
  }, [
    catalogLoading,
    doctors,
    branches,
    bookableServices,
    specializationOptions,
    searchParams,
  ]);

  useEffect(() => {
    if (step !== 2 || !serviceId) return;
    if (branchesForService.length === 1) {
      const only = branchesForService[0];
      if (branchId !== only.id) setBranchId(only.id);
      setStep(3);
    }
  }, [step, serviceId, branchesForService, branchId]);

  useEffect(() => {
    if (step !== 3 || anyDoctor || !serviceId || !branchId) return;
    if (doctorsForBooking.length === 1) {
      setDoctorId(doctorsForBooking[0].id);
      setStep(4);
    }
  }, [step, anyDoctor, serviceId, branchId, doctorsForBooking]);

  useEffect(() => {
    if (
      doctorId &&
      !anyDoctor &&
      step < 4 &&
      !doctorsForBooking.some((d) => d.id === doctorId)
    ) {
      setDoctorId("");
      setSlotIso("");
    }
  }, [step, doctorId, anyDoctor, doctorsForBooking]);

  useEffect(() => {
    if (step !== 4) {
      slotsScanRef.current = false;
      return;
    }
    if (!branchId || !serviceId || !date) return;

    if (!anyDoctor && !doctorId) {
      setSlots([]);
      setAggregated([]);
      setSlotsMessage("Сначала выберите тренера на предыдущем шаге");
      setNearestSlotDate("");
      setLoadingSlots(false);
      return;
    }

    let cancelled = false;
    setLoadingSlots(true);
    setSlotIso("");
    setErr("");
    setSlotsMessage("");
    setNearestSlotDate("");

    const loadForDate = (dateStr) =>
      anyDoctor
        ? clinicAPI.getPublicAggregatedSlots(branchId, dateStr, serviceId)
        : clinicAPI.getPublicSlots(doctorId, branchId, dateStr, serviceId);

    const applyResult = (r, dateStr) => {
      if (anyDoctor) {
        setAggregated(r.doctors || []);
        setSlots([]);
      } else {
        setSlots(r.slots || []);
        setAggregated([]);
        setSubstituteHint(r.substitute_doctor || null);
      }
      setSlotsMessage(r.message || "");
      if (dateStr !== date) setDate(dateStr);
    };

    const findNearestDate = async () => {
      for (const d of dates) {
        if (d.value === date) continue;
        try {
          const r = await loadForDate(d.value);
          if (cancelled) return;
          const hasSlots = anyDoctor
            ? (r.doctors || []).some((row) => (row.slots || []).length > 0)
            : (r.slots || []).length > 0;
          if (hasSlots) {
            setNearestSlotDate(d.value);
            return;
          }
        } catch {
          /* ignore per-date errors */
        }
      }
    };

    loadForDate(date)
      .then(async (r) => {
        if (cancelled) return;
        const hasSlots = anyDoctor
          ? (r.doctors || []).some((row) => (row.slots || []).length > 0)
          : (r.slots || []).length > 0;

        if (hasSlots) {
          applyResult(r, date);
          return;
        }

        applyResult(r, date);

        if (!slotsScanRef.current) {
          slotsScanRef.current = true;
          const nextDate = dates.find((d) => d.value !== date);
          if (nextDate) {
            try {
              const near = await loadForDate(nextDate.value);
              if (cancelled) return;
              const nearHasSlots = anyDoctor
                ? (near.doctors || []).some((row) => (row.slots || []).length > 0)
                : (near.slots || []).length > 0;
              if (nearHasSlots) {
                applyResult(near, nextDate.value);
                return;
              }
            } catch {
              /* fall through */
            }
          }
        }

        await findNearestDate();
      })
      .catch(() => setErr("Не удалось загрузить свободное время"))
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, branchId, serviceId, date, doctorId, anyDoctor, dates]);

  const applyPatch = (patch, resetLevel) => {
    let next = {
      specializationId,
      serviceId,
      branchId,
      doctorId,
      anyDoctor,
      slotIso,
    };
    Object.assign(next, patch);
    if (resetLevel != null) next = resetFrom(resetLevel, next);

    setSpecializationId(next.specializationId);
    setServiceId(next.serviceId);
    setBranchId(next.branchId);
    setDoctorId(next.doctorId);
    setAnyDoctor(next.anyDoctor);
    setSlotIso(next.slotIso);
    return next;
  };

  const resolveNextStep = (fromStep, state) => {
    let next = fromStep + 1;
    const availBranches = getBranchesForService(
      state.serviceId,
      branches,
      bookableServices,
      doctors,
      state.specializationId,
    );
    if (next === 2 && state.branchId && availBranches.length === 1) {
      next = 3;
    }
    const availDoctors = getDoctorsForServiceBranch(
      state.serviceId,
      state.branchId,
      doctors,
      state.specializationId,
    );
    if (next === 3 && state.doctorId && !state.anyDoctor && availDoctors.length === 1) {
      next = 4;
    }
    return Math.min(next, 5);
  };

  const selectAndAdvance = (patch, resetLevel) => {
    setErr("");
    const merged = applyPatch(patch, resetLevel);
    const currentStep = step;

    if (currentStep === 1 && patch.serviceId) {
    const availBranches = getBranchesForService(
      merged.serviceId,
      branches,
      bookableServices,
      doctors,
      merged.specializationId,
    );
      if (availBranches.length === 0) {
        setErr("Нет залов, где оказывают эту услугу");
        return;
      }
      if (
        merged.branchId &&
        !availBranches.some((b) => b.id === merged.branchId) &&
        availBranches[0]
      ) {
        merged.branchId = availBranches[0].id;
        setBranchId(availBranches[0].id);
      }
      if (availBranches.length === 1) {
        merged.branchId = availBranches[0].id;
        setBranchId(availBranches[0].id);
      }
    }

    if (currentStep === 2 && patch.branchId) {
      const availDoctors = getDoctorsForServiceBranch(
        merged.serviceId,
        merged.branchId,
        doctors,
        patch.specializationId ?? specializationId,
      );
      if (availDoctors.length === 0) {
        setErr("Нет тренеров для этой услуги в выбранном зале");
        return;
      }
      if (availDoctors.length === 1 && !merged.anyDoctor) {
        merged.doctorId = availDoctors[0].id;
        setDoctorId(availDoctors[0].id);
      }
    }

    if (currentStep < 4) {
      setStep(resolveNextStep(currentStep, merged));
    }
  };

  const pickSlot = (iso, docId = null) => {
    setSlotIso(iso);
    if (docId) {
      setDoctorId(docId);
      setAnyDoctor(false);
    }
    setStep(5);
  };

  const pickDoctor = (id) => {
    if (!branchId) {
      setErr("Сначала выберите зал");
      setStep(2);
      return;
    }
    if (!serviceId) {
      setErr("Сначала выберите услугу");
      setStep(1);
      return;
    }
    setErr("");
    setAnyDoctor(false);
    setDoctorId(id);
    setSlotIso("");
    setStep(4);
  };

  const pickAnyDoctor = () => {
    if (!branchId) {
      setErr("Сначала выберите зал");
      setStep(2);
      return;
    }
    if (!serviceId) {
      setErr("Сначала выберите услугу");
      setStep(1);
      return;
    }
    setErr("");
    setAnyDoctor(true);
    setDoctorId("");
    setSlotIso("");
    setStep(4);
  };

  const goToStep = (target) => {
    if (target < step) setStep(target);
  };

  const toggleFavorite = async (id, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      if (favoriteIds.has(id)) {
        await clinicAPI.removeFavoriteDoctor(id);
        setFavoriteIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      } else {
        await clinicAPI.addFavoriteDoctor(id);
        setFavoriteIds((prev) => new Set(prev).add(id));
      }
    } catch (ex) {
      toast.error(ex.response?.data?.error || "Ошибка");
    }
  };

  const joinWaitlist = async () => {
    const targetDoctor = anyDoctor
      ? doctorsForBooking[0]?.id
      : doctorId;
    if (!targetDoctor) {
      toast.error("Выберите тренера");
      return;
    }
    try {
      await clinicAPI.addMyWaitlist({
        doctor_id: targetDoctor,
        service_id: serviceId,
        branch_id: branchId,
        preferred_date: date,
      });
      toast.success("Вы в очереди ожидания");
    } catch (ex) {
      toast.error(ex.response?.data?.error || "Ошибка");
    }
  };

  const confirm = async () => {
    if (!isAuth || !isClient) {
      navigate("/login", { state: { from: "/app/book" } });
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await clinicAPI.createAppointment({
        doctor_id: doctorId,
        branch_id: branchId,
        service_id: serviceId,
        start_datetime: slotIso,
        dependent_id: dependentId || undefined,
      });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Не удалось записаться");
    } finally {
      setSubmitting(false);
    }
  };

  const progressParts = [
    selectedSpecialization?.name,
    selectedService?.name,
    selectedBranch?.name,
    anyDoctor ? "Любой тренер" : selectedDoctor?.full_name,
    slotIso ? `${formatDateRu(slotIso)} ${formatTimeRu(slotIso)}` : null,
  ].filter(Boolean);

  if (done) {
    const doneCard = (
      <Card title="Вы записаны">
        <p className={s.bookDoneText}>
          Запись подтверждена. Детали — в разделе «Мои визиты».
        </p>
        {embedded ? (
          <Button type="button" onClick={() => onBooked?.()}>
            Мои визиты
          </Button>
        ) : (
          <Link to="/app/visits">
            <Button>Мои визиты</Button>
          </Link>
        )}
      </Card>
    );
    if (embedded) {
      return <div className={s.portalBookDone}>{doneCard}</div>;
    }
    return (
      <div className={s.bookPage}>
        <header className={s.bookHeader}>
          <img src={logo} alt="" height={36} />
          <span className={s.bookClinicName}>{clinic?.name}</span>
        </header>
        <div className={s.portalBookDone}>{doneCard}</div>
      </div>
    );
  }

  const stepBar = embedded ? null : (
    <div className={s.bookWizardSteps}>
      {STEPS.map((label, i) => {
        const isActive = i === step;
        const isDone = i < step;
        const className = `${s.bookWizardStep} ${
          isActive
            ? s.bookWizardStepActive
            : isDone
              ? s.bookWizardStepDone
              : ""
        }`;
        if (isDone) {
          return (
            <button
              key={label}
              type="button"
              className={className}
              onClick={() => goToStep(i)}
            >
              {label}
            </button>
          );
        }
        return (
          <div key={label} className={className}>
            {label}
          </div>
        );
      })}
    </div>
  );

  const wizard = (
    <div className={embedded ? s.portalBookWizard : s.bookWizard}>
      {stepBar}

      {progressParts.length > 0 && step > 0 && !embedded ? (
        <p className={s.bookWizardSummary}>{progressParts.join(" → ")}</p>
      ) : null}

      {err ? <div className={s.alertError}>{err}</div> : null}

      <div className={embedded ? s.portalBookStepBody : s.bookWizardBody}>
        {catalogLoading ? (
          <Skeleton height={200} />
        ) : (
          <>
            {embedded && !appMode ? (
              <header className={s.portalBookStepHead}>
                <span className={s.portalBookStepEyebrow}>
                  Шаг {step + 1} из {STEPS.length}
                </span>
                <h2 className={s.portalBookStepTitleMain}>{STEP_HINTS[step]}</h2>
              </header>
            ) : !embedded ? (
              <h3 className={s.bookWizardStepTitle}>{STEP_HINTS[step]}</h3>
            ) : null}

            {step === 0 && (
              <div className={embedded ? s.portalBookSpecGrid : s.bookWizardSpecGrid}>
                {specializationOptions.length === 0 ? (
                  <p className={s.docEmptyHint}>
                    Нет услуг с назначенными тренерами. Обратитесь в регистратуру.
                  </p>
                ) : (
                  specializationOptions.map((sp) => (
                    <button
                      key={sp.id}
                      type="button"
                      className={
                        embedded
                          ? `${s.portalBookSpecTile} ${
                              specializationId === sp.id
                                ? s.portalBookSpecTileSelected
                                : ""
                            }`
                          : specializationId === sp.id
                            ? s.bookWizardSpecTileSelected
                            : s.bookWizardSpecTile
                      }
                      onClick={() =>
                        selectAndAdvance({ specializationId: sp.id }, 2)
                      }
                    >
                      {embedded ? (
                        <>
                          <span className={s.portalBookSpecIcon} aria-hidden>
                            {specIcon(sp.name)}
                          </span>
                          <span className={s.portalBookSpecBody}>
                            <span className={s.portalBookSpecName}>{sp.name}</span>
                            <span className={s.portalBookSpecCount}>
                              {serviceCountLabel(sp.count)}
                            </span>
                          </span>
                          <span className={s.portalBookSpecArrow} aria-hidden>
                            →
                          </span>
                        </>
                      ) : (
                        <>
                          <span className={s.bookWizardSpecName}>{sp.name}</span>
                          <span className={s.bookWizardSpecCount}>
                            {serviceCountLabel(sp.count)}
                          </span>
                        </>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}

            {step === 1 && (
              <>
                {selectedSpecialization ? (
                  <div className={s.bookWizardMeta}>
                    <span className={s.bookWizardBadge}>
                      {selectedSpecialization.name}
                    </span>
                  </div>
                ) : null}
                <div className={s.bookWizardList}>
                  {servicesForSpec.length === 0 ? (
                    <p className={s.docEmptyHint}>
                      Нет услуг в выбранном направлении
                    </p>
                  ) : (
                    servicesForSpec.map((sv) => (
                      <button
                        key={sv.id}
                        type="button"
                        className={
                          serviceId === sv.id
                            ? s.bookWizardListItemSelected
                            : s.bookWizardListItem
                        }
                        onClick={() =>
                          selectAndAdvance({ serviceId: sv.id }, 3)
                        }
                      >
                        <span>
                          <div className={s.bookWizardListMain}>
                            {formatServiceBookingLabel(
                              sv,
                              specializationId,
                              specializations,
                            )}
                          </div>
                          {sv.description ? (
                            <div className={s.bookWizardListMeta}>
                              {sv.description}
                            </div>
                          ) : null}
                          {sv.duration_minutes ? (
                            <div className={s.bookWizardListMeta}>
                              {sv.duration_minutes} мин
                            </div>
                          ) : null}
                        </span>
                        {sv.price != null ? (
                          <span className={s.bookWizardPrice}>{sv.price} ₽</span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {step === 2 && (
              <div className={s.bookWizardList}>
                {branchesForService.length === 0 ? (
                  <p className={s.docEmptyHint}>
                    Нет залов для выбранной услуги
                  </p>
                ) : (
                  branchesForService.map((br) => (
                    <button
                      key={br.id}
                      type="button"
                      className={
                        branchId === br.id
                          ? s.bookWizardListItemSelected
                          : s.bookWizardListItem
                      }
                      onClick={() =>
                        selectAndAdvance({ branchId: br.id }, 4)
                      }
                    >
                      <span>
                        <div className={s.bookWizardListMain}>{br.name}</div>
                        {br.address || br.metro ? (
                          <div className={s.bookWizardBranchMeta}>
                            {[br.address, br.metro && `м. ${br.metro}`]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        ) : null}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}

            {step === 3 && (
              <>
                <button
                  type="button"
                  className={`${s.bookWizardListItem} ${s.bookWizardAnyDoctor} ${
                    anyDoctor ? s.bookWizardListItemSelected : ""
                  }`}
                  onClick={pickAnyDoctor}
                >
                  <span>
                    <div className={s.bookWizardListMain}>
                      Любой свободный тренер
                    </div>
                    <div className={s.bookWizardListMeta}>
                      Покажем ближайшие слоты
                      {selectedSpecialization
                        ? ` среди тренеров «${selectedSpecialization.name}»`
                        : " по всем специалистам направления"}
                    </div>
                  </span>
                </button>

                {doctorsForBooking.length === 0 ? (
                  <p className={s.docEmptyHint}>
                    {selectedSpecialization
                      ? `Нет тренеров «${selectedSpecialization.name}» с этой услугой в зале`
                      : "Нет тренеров с этой услугой в зале"}
                  </p>
                ) : (
                  <div className={s.bookWizardDoctorGrid}>
                    {doctorsForBooking.map((d) => (
                      <DoctorBookCard
                        key={d.id}
                        doctor={d}
                        selected={doctorId === d.id && !anyDoctor}
                        onSelect={() => pickDoctor(d.id)}
                        favoriteSlot={
                          isAuth && isClient ? (
                            <button
                              type="button"
                              className={`${s.portalFavBtn} ${s.bookWizardFavOnCard} ${
                                favoriteIds.has(d.id) ? s.portalFavBtnOn : ""
                              }`}
                              title="В избранное"
                              onClick={(e) => toggleFavorite(d.id, e)}
                            >
                              ★
                            </button>
                          ) : null
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {step === 4 && (
              <>
                <div className={s.bookWizardMeta}>
                  {selectedService ? (
                    <span className={s.bookWizardBadge}>
                      {selectedService.name}
                    </span>
                  ) : null}
                  {anyDoctor ? (
                    <span className={s.bookWizardBadge}>Любой тренер</span>
                  ) : selectedDoctor ? (
                    <span className={s.bookWizardBadge}>
                      {selectedDoctor.full_name}
                    </span>
                  ) : null}
                </div>
                <div className={s.dateStrip}>
                  {dates.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`${s.dateChip} ${
                        date === d.value ? s.dateChipSelected : ""
                      }`}
                      onClick={() => setDate(d.value)}
                    >
                      <div className={s.dateChipDay}>{d.day}</div>
                      <div className={s.dateChipNum}>{d.num}</div>
                    </button>
                  ))}
                </div>
                {loadingSlots ? (
                  <Skeleton height={120} />
                ) : anyDoctor ? (
                  <div className={s.slotByDoctor}>
                    {aggregatedForBooking.length === 0 ? (
                      <p className={s.docEmptyHint}>
                        {slotsMessage ||
                          "Нет свободного времени на эту дату"}
                      </p>
                    ) : (
                      aggregatedForBooking.map(({ doctor: doc, slots: docSlots }) => (
                        <div key={doc.id} className={s.slotDoctorBlock}>
                          <div className={s.slotDoctorName}>{doc.full_name}</div>
                          <div className={s.timeGrid}>
                            {docSlots.map((sl) => (
                              <button
                                key={sl.start_datetime}
                                type="button"
                                className={`${s.timeSlot} ${
                                  slotIso === sl.start_datetime &&
                                  doctorId === doc.id
                                    ? s.timeSlotSelected
                                    : ""
                                }`}
                                onClick={() =>
                                  pickSlot(sl.start_datetime, doc.id)
                                }
                              >
                                {formatTimeRu(sl.start_datetime)}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : slots.length === 0 ? (
                  <div>
                    <p className={s.docEmptyHint}>
                      {slotsMessage ||
                        "Нет свободного времени на эту дату"}
                    </p>
                    {nearestSlotDate && nearestSlotDate !== date ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        style={{ marginTop: 12, marginRight: 8 }}
                        onClick={() => setDate(nearestSlotDate)}
                      >
                        Ближайшая запись: {formatDateRu(nearestSlotDate)}
                      </Button>
                    ) : null}
                    {isAuth && isClient ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        style={{ marginTop: 12 }}
                        onClick={joinWaitlist}
                      >
                        Встать в очередь ожидания
                      </Button>
                    ) : null}
                    {substituteHint ? (
                      <div className={s.substituteBanner}>
                        <p>
                          Тренер недоступен. Замена:{" "}
                          <strong>{substituteHint.full_name}</strong>
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setDoctorId(substituteHint.id);
                            setAnyDoctor(false);
                          }}
                        >
                          Выбрать {substituteHint.full_name}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className={s.timeGrid}>
                    {slots.map((sl) => (
                      <button
                        key={sl.start_datetime}
                        type="button"
                        className={`${s.timeSlot} ${
                          slotIso === sl.start_datetime
                            ? s.timeSlotSelected
                            : ""
                        }`}
                        onClick={() => pickSlot(sl.start_datetime)}
                      >
                        {formatTimeRu(sl.start_datetime)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {step === 5 && (
              <div className={s.confirmBox}>
                <div className={s.confirmRow}>
                  <span className={s.confirmLabel}>Направление</span>
                  <span>{selectedSpecialization?.name || "—"}</span>
                </div>
                <div className={s.confirmRow}>
                  <span className={s.confirmLabel}>Услуга</span>
                  <span>{selectedService?.name}</span>
                </div>
                <div className={s.confirmRow}>
                  <span className={s.confirmLabel}>Зал</span>
                  <span>{selectedBranch?.name}</span>
                </div>
                <div className={s.confirmRow}>
                  <span className={s.confirmLabel}>Тренер</span>
                  <span>{selectedDoctor?.full_name}</span>
                </div>
                <div className={s.confirmRow}>
                  <span className={s.confirmLabel}>Когда</span>
                  <span>
                    {formatDateRu(slotIso, { weekday: "long" })} в{" "}
                    {formatTimeRu(slotIso)}
                  </span>
                </div>
                {dependents.length > 0 ? (
                  <div className={s.confirmRow}>
                    <span className={s.confirmLabel}>Клиент</span>
                    <select
                      className={s.portalConfirmSelect}
                      value={dependentId}
                      onChange={(e) => setDependentId(e.target.value)}
                    >
                      <option value="">Я сам(а)</option>
                      {dependents.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {!isAuth && (
                  <p className={`${s.alertError} ${s.alertSpaced}`}>
                    Для завершения записи войдите или зарегистрируйтесь.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className={embedded ? (appMode ? s.clientBookFooter : s.portalBookFooter) : s.bookWizardFooter}>
        {step > 0 && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setErr("");
              setStep((n) => n - 1);
            }}
          >
            Назад
          </Button>
        )}
        {step === 5 ? (
          <Button type="button" onClick={confirm} disabled={submitting}>
            {submitting
              ? "Запись…"
              : isAuth
                ? "Записаться"
                : "Войти и записаться"}
          </Button>
        ) : step === 4 ? (
          slotIso ? (
            <Button type="button" onClick={() => setStep(5)}>
              К подтверждению
            </Button>
          ) : null
        ) : null}
      </div>
    </div>
  );

  if (embedded && appMode) {
    return (
      <div className={s.clientBookPage}>
        <ClientBookProgress step={step} label={STEP_HINTS[step]} />
        {progressParts.length > 0 ? (
          <div className={s.clientBookChoice}>
            {progressParts.map((part) => (
              <span key={part} className={s.clientBookChoiceChip}>
                {part}
              </span>
            ))}
          </div>
        ) : null}
        <div className={s.clientBookBody}>{wizard}</div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className={s.portalBookPage}>
        <div className={s.portalBookShell}>
          <aside className={s.portalBookAside}>
            <BookStepper step={step} onGo={goToStep} />
            {progressParts.length > 0 ? (
              <div className={s.portalBookChoice}>
                <p className={s.portalBookChoiceTitle}>Ваш выбор</p>
                <ul className={s.portalBookChoiceList}>
                  {progressParts.map((part) => (
                    <li key={part}>{part}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>
          <section className={s.portalBookMain}>
            <div className={s.portalBookMainGlow} aria-hidden />
            <div className={s.portalBookMainInner}>{wizard}</div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={s.bookPage}>
      <header className={s.bookHeader}>
        <Link to="/">
          <img src={logo} alt="" height={36} />
        </Link>
        <div>
          <div className={s.bookClinicName}>{clinic?.name || "Клуб"}</div>
          <div className={s.bookSubtitle}>Запись на занятие онлайн</div>
        </div>
        {!isAuth ? (
          <Link to="/login">
            <Button variant="secondary" size="sm">
              Войти
            </Button>
          </Link>
        ) : (
          <Link to="/app">
            <Button variant="secondary" size="sm">
              Кабинет
            </Button>
          </Link>
        )}
      </header>
      {wizard}
    </div>
  );
}

export default function PatientBookPage({ embedOnly = false } = {}) {
  return (
    <ToastProvider>
      <PatientBookContent embedded={embedOnly} />
    </ToastProvider>
  );
}
